import { BrowserRouter } from 'react-router';
import { App } from './app.js';
import { DocumentMetaProvider } from './document-meta.js';
import { AppErrorBoundary } from './error-boundary.js';
import { fallbackCatalogue, LocaleProvider } from './locale.js';
import { ServerError } from './routes/server-error.js';

/**
 * The provider/boundary composition the app is mounted with (issue #376).
 *
 * It lives here rather than inline in `main.tsx` so it can be RENDERED in a
 * test: the ordering below is the actual control, and a test that restates it
 * would drift from the real one. `main.tsx` now only creates the root.
 *
 * THE ORDERING IS THE POINT. An error boundary catches only its descendants,
 * so the OUTERMOST instance must sit ABOVE `LocaleProvider` and
 * `DocumentMetaProvider` to catch a throw from either (cross-review F001). Its
 * fallback therefore passes a catalogue resolved from the document rather than
 * the context that failed.
 */
export function AppRoot() {
  return (
    <AppErrorBoundary fallback={() => <ServerError catalog={fallbackCatalogue()} />}>
      <LocaleProvider>
        <BrowserRouter>
          <DocumentMetaProvider>
            {/*
              The shell boundary: inside both providers and inside the router,
              so a throw in the header or footer is still replaced by a
              localised page. `App` holds a third, pathname-keyed boundary
              around the route content. Retry reloads the document, the only
              recovery that re-requests a `React.lazy` chunk whose import
              rejected (cross-review F003).
            */}
            <AppErrorBoundary
              fallback={() => <ServerError onRetry={() => window.location.reload()} />}
            >
              <App />
            </AppErrorBoundary>
          </DocumentMetaProvider>
        </BrowserRouter>
      </LocaleProvider>
    </AppErrorBoundary>
  );
}
